import type { InjectionKey, Ref } from 'vue'

import { useFullscreen } from '@vueuse/core'

export const PRESENTER_WINDOW_KEY: InjectionKey<Ref<null | Window>> = Symbol('game:presenter-window')

export function provideGameFullscreen() {
  const presenterWindow = ref<null | Window>(null)
  provide(PRESENTER_WINDOW_KEY, presenterWindow)
  return presenterWindow
}

export function useGameFullscreen() {
  const presenterWindow = inject(PRESENTER_WINDOW_KEY, null)
  const { isFullscreen: isSelfFullscreen, toggle: toggleSelf } = useFullscreen()

  const hasPresenterTarget = computed(() => {
    const target = presenterWindow?.value
    return !!target && !target.closed
  })

  const isPresenterFullscreen = ref(false)

  function syncPresenterFullscreenState() {
    const target = presenterWindow?.value
    if (!target || target.closed) {
      isPresenterFullscreen.value = false
      return
    }
    try {
      isPresenterFullscreen.value = !!target.document.fullscreenElement
    } catch {
      isPresenterFullscreen.value = false
    }
  }

  let pollTimer: null | ReturnType<typeof setInterval> = null

  function startPollingPresenterFullscreen() {
    if (!import.meta.client || pollTimer !== null) {
      return
    }
    pollTimer = setInterval(syncPresenterFullscreenState, 400)
  }

  function stopPollingPresenterFullscreen() {
    if (pollTimer !== null) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  if (import.meta.client && presenterWindow) {
    watch(
      () => presenterWindow.value,
      target => {
        syncPresenterFullscreenState()
        if (target && !target.closed) {
          startPollingPresenterFullscreen()
        } else {
          stopPollingPresenterFullscreen()
        }
      },
      { immediate: true },
    )
    onBeforeUnmount(stopPollingPresenterFullscreen)
  }

  const isFullscreen = computed(() => {
    return hasPresenterTarget.value ? isPresenterFullscreen.value : isSelfFullscreen.value
  })

  async function togglePresenterFullscreen() {
    const target = presenterWindow?.value
    if (!target || target.closed) {
      return
    }
    try {
      const doc = target.document
      if (doc.fullscreenElement) {
        await doc.exitFullscreen()
      } else {
        await doc.documentElement.requestFullscreen({ navigationUI: 'hide' })
      }
      syncPresenterFullscreenState()
    } catch {
      // Fullscreen can be blocked by missing user-activation on the popup
      // document. Swallow — the user will see nothing changed and can try again.
    }
  }

  async function toggle() {
    if (hasPresenterTarget.value) {
      await togglePresenterFullscreen()
      return
    }
    await toggleSelf()
  }

  return {
    hasPresenterTarget,
    isFullscreen,
    toggle,
  }
}
